import { AttributeRef } from "generated/mos/entity";
import { ResolvedValueResult } from "generated/mos/linkedfieldresolver";
import { Placement, SpacePlacement } from "generated/mos/placement";
import { Space } from "generated/mos/structure";
import { permissiveAreValuesEqual } from "helpers/core";
import { LinkableValue } from "helpers/linkables";


/**
 * Determine if a field in the stop-update form is dirty by comparing the current value of a form field in the UI with the field's stored value.
 * @param resolvedValues An array of ResolvedValueResult.Entity objects so that values which contain entity refs can be resolved before comparing
 * @param value The current value of a form field in the UI
 * @param formValue The currently stored value of the field in the form object
 * @returns Boolean
 */
export const compareValueInequality = (
  resolvedValues: ReadonlyArray<ResolvedValueResult.Entity> | undefined,
  value: LinkableValue,
  formValue: Space.Ref | string | undefined
) => {

  let isNotEqual = false;

  switch (narrowLinkableValueType(value)) {
    case 'string': {
      isNotEqual = value !== formValue;
      break;
    }
    case 'placement': {
      const entity = value as Placement.Entity;
      const placement = (entity.placement as SpacePlacement.Entity).spaceRef;
      isNotEqual = !permissiveAreValuesEqual(placement, formValue);
      break;
    }
    default: {
      const attributeRef = value as AttributeRef.Entity;
      const resolvedValue = getResolvedAttributeRefResult(attributeRef, resolvedValues);
      
      if (!resolvedValue) break;
      
      switch (resolvedValue.type) {
        case ResolvedValueResult.Value.PlacementValue.refName:
          // Note: Assumes that the only Placement entity found will be a SpacePlacement
          
          /* eslint-disable no-case-declarations */
          const placementWrapper = resolvedValue.placement;
          const spacePlacement = placementWrapper && placementWrapper.placement as SpacePlacement.Entity;
          const spaceRef = spacePlacement && spacePlacement.spaceRef;          
          /* eslint-enable no-case-declarations */
          isNotEqual = !permissiveAreValuesEqual(spaceRef, formValue);
          break;
        case ResolvedValueResult.Value.StringValue.refName:
          isNotEqual = resolvedValue.string !== formValue;
          break;
        default:
          console.warn(`Unable to compare valueInequality for form field:`, value)
      }
    }

  }

  return isNotEqual;
};


/**
 * Takes an AtrributeRef.Entity and finds its resolved value from an array of ResolvedValueResult.Entity objects
 */
const getResolvedAttributeRefResult = (ref: AttributeRef.Entity, resolvedValues: ReadonlyArray<ResolvedValueResult.Entity> = [] ) => {
  
  const matchedItem = resolvedValues.find((item) => (
    item.result &&
    item.result.targetFieldRef &&
    item.result.targetFieldRef.attributeName === ref.attributeName
    )); 
  
    const resolvedValueResult = matchedItem && matchedItem.result ? matchedItem.result as ResolvedValueResult.Value.Entity : undefined;
    return resolvedValueResult && resolvedValueResult.targetValue;
}

/**
 * Receives a LinkableValue argument and returns a 'string', 'placement' or 'default' depending on its actual type. Used by the compareValueInequality function to work out how to parse the specific value object it receives.
 * @param value LinkableValue
 * @returns 'string' if value is a string; 'placement' if value is a Placement.Entity; ot 'default' for any other type
 */
const narrowLinkableValueType = (value: LinkableValue): 'string' | 'placement' | 'default' => {
  if (typeof value === 'string') return 'string';
  if ((value as Placement.Entity).placement) return 'placement';
  return 'default';
}